import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { all, put, select, takeLatest } from 'redux-saga/effects'
import { type ApiError, type Question } from '@pbt/pbt-ui-components'

import * as API from '../../api'
import Features from '../../constants/features'
import { getErrorMessage } from '../../utils/errors'
import { flattenFollowUpQuestions } from '../../utils/questionUtils'
import type { RootState } from '../index'
// @ts-ignore
import requestAPI from '../sagas/requestAPI'
import { getFeatureToggle } from './constants'

type QuestionsState = {
  error: string | null
  isLoading: boolean
  isSaving: boolean
  log: Question[] | []
}

export const INITIAL_STATE: QuestionsState = {
  log: [],
  error: null,
  isLoading: false,
  isSaving: false,
}

const questionsSlice = createSlice({
  name: 'questions',
  initialState: INITIAL_STATE,
  reducers: {
    fetchQuestionsLog: (
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      action: PayloadAction<{ appointmentId: string }>,
    ) => {
      state.isLoading = true
      state.error = null
    },
    fetchQuestionsLogSuccess: (state, action: PayloadAction<Question[]>) => {
      state.log = action.payload
      state.isLoading = false
    },
    fetchQuestionsLogFailure: (state, action: PayloadAction<ApiError>) => {
      state.error = getErrorMessage(action.payload)
      state.isLoading = false
    },
    saveQuestionsLog: (
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      action: PayloadAction<{
        appointmentId: string
        log: Question[]
      }>,
    ) => {
      state.isSaving = true
      state.error = null
    },
    saveQuestionsLogSuccess: (state, action: PayloadAction<Question[]>) => {
      state.log = action.payload
      state.isSaving = false
    },
    saveQuestionsLogFailure: (state, action: PayloadAction<ApiError>) => {
      state.error = getErrorMessage(action.payload)
      state.isSaving = false
    },
  },
})

export const questionsReducer = questionsSlice.reducer
export const {
  fetchQuestionsLog,
  fetchQuestionsLogSuccess,
  fetchQuestionsLogFailure,
  saveQuestionsLog,
  saveQuestionsLogSuccess,
  saveQuestionsLogFailure,
} = questionsSlice.actions

export const getQuestions = (state: RootState) => state.questions
export const getQuestionsLog = (state: RootState) => getQuestions(state).log
export const getQuestionsIsLoading = (state: RootState) =>
  getQuestions(state).isLoading
export const getQuestionsError = (state: RootState) => getQuestions(state).error
export const getQuestionsIsSaving = (state: RootState) =>
  getQuestions(state).isSaving

export function* sagaFetchQuestionsLog({
  payload: { appointmentId },
}: ReturnType<typeof fetchQuestionsLog>) {
  try {
    const log = yield* requestAPI(API.fetchQuestionsLog, appointmentId)
    yield put(fetchQuestionsLogSuccess(log))
  } catch (error) {
    yield put(fetchQuestionsLogFailure(error as ApiError))
  }
}

export function* sagaSaveQuestionsLog({
  payload: { appointmentId, log },
}: ReturnType<typeof saveQuestionsLog>) {
  try {
    const isFollowUpQuestionsEnabled: boolean = yield select(
      getFeatureToggle(Features.FOLLOW_UP_QUESTIONS),
    )
    const logToSend = isFollowUpQuestionsEnabled
      ? flattenFollowUpQuestions(log)
      : log

    const result = yield* requestAPI(
      API.saveQuestionsLog,
      appointmentId,
      logToSend,
    )
    yield put(saveQuestionsLogSuccess(result))
  } catch (error) {
    yield put(saveQuestionsLogFailure(error as ApiError))
  }
}

function* watchFetchQuestionsLog() {
  yield takeLatest(fetchQuestionsLog.type, sagaFetchQuestionsLog)
}

function* watchSaveQuestionsLog() {
  yield takeLatest(saveQuestionsLog.type, sagaSaveQuestionsLog)
}

export function* questionsSaga() {
  yield all([watchFetchQuestionsLog(), watchSaveQuestionsLog()])
}
